// ═══════════════════════════════════════════════════════════════
// TestSubscribeService — Activation gratuite d'abonnement (mode TEST)
//
// Point unique d'activation d'abonnement dans toute l'application.
// En mode TEST : active l'abonnement instantanément sans passer par Stripe/IAP.
// ═══════════════════════════════════════════════════════════════

import { Alert } from 'react-native';
import AWS_CONFIG from '../config/aws_config';

// ═══ ACTIVER LE MODE TEST ICI ═══
// Passer à false pour désactiver l'activation gratuite et passer par
// RevenueCat + Google Play Store (vrais achats avec facturation sandbox).
const TEST_MODE = false;

/**
 * Récupère un token d'authentification valide.
 */
async function getValidTokenFallback(user) {
  if (user?.accessToken) return user.accessToken;
  try {
    const { getAuthToken } = await import('./authHelpers');
    return await getAuthToken();
  } catch {
    return null;
  }
}

/**
 * Active un abonnement en mode TEST gratuitement.
 *
 * @param {string} planId - ex. "premium", "premium_yearly", "basic", "gold", "free"
 * @param {Object} user - utilisateur courant (avec accessToken)
 * @param {Function} updateUserProfile - optionnel, pour mettre à jour l'user dans le contexte
 * @param {Object} options - { silent: bool (ne pas afficher d'alerte) }
 * @returns {Promise<{success: boolean, tier: string, period: string}>}
 */
export async function subscribeTestMode(planId, user, updateUserProfile, options = {}) {
  const { silent = false } = options;
  try {
    const tier   = planId.replace('_yearly', '');
    const period = planId.includes('_yearly') ? 'yearly' : 'monthly';

    // 1. Mettre à jour le tier en DB via /set-tier
    const token = await getValidTokenFallback(user);
    if (token) {
      try {
        await fetch(AWS_CONFIG.apiUrl + '/set-tier', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
          },
          body: JSON.stringify({
            tier,
            period,
            platform: 'test',
            payment_ref: 'test_' + Date.now(),
          }),
        });
      } catch(e) {
        console.warn('[TestSubscribe] /set-tier failed:', e.message);
      }
    }

    // 2. Sauvegarder localement (cache AsyncStorage)
    try {
      const { saveTierLocally } = require('./PremiumService');
      const uid = user?.id || user?.sub;
      if (uid) await saveTierLocally(uid, tier);
    } catch {}

    // 3. Mettre à jour le contexte utilisateur
    if (updateUserProfile) {
      try { updateUserProfile({ tier }); } catch {}
    }

    // 4. Feedback utilisateur
    if (!silent) {
      const labels = {
        free: 'Gratuit',
        basic: 'Basic',
        premium: 'Premium',
        gold: 'Gold',
      };
      const label = labels[tier] || tier.toUpperCase();
      Alert.alert(
        '✓ Plan activé',
        `Votre plan ${label}${period === 'yearly' ? ' annuel' : ''} est désormais actif.\n\n[Mode TEST]`,
      );
    }

    console.log(`[TestSubscribe] ✅ Activated ${tier} (${period}) for user=${user?.id}`);
    return { success: true, tier, period };
  } catch (e) {
    console.error('[TestSubscribe] error:', e);
    if (!silent) {
      Alert.alert('Erreur', e.message || "Impossible d'activer l'abonnement.");
    }
    return { success: false, error: e.message };
  }
}

/**
 * Version qui appelle le vrai service de paiement (RevenueCat) en production,
 * sinon TEST mode. À utiliser partout dans l'app pour garantir un comportement uniforme.
 *
 * Flow :
 *  - TEST_MODE=true             → subscribeTestMode (gratuit, instantané, pour debug)
 *  - TEST_MODE=false (prod)     → IAPService.purchaseSubscription (RC → Google Play)
 */
export async function activateSubscription(planId, user, updateUserProfile) {
  if (TEST_MODE) {
    return subscribeTestMode(planId, user, updateUserProfile);
  }

  // ═══ Production : vrai achat via RevenueCat → Google Play ═══
  // IAPService.purchaseSubscription(tier, period, user) :
  //  - Ouvre le popup d'achat Google Play
  //  - RC valide l'achat côté serveur
  //  - Envoie webhook vers notre Lambda qui met à jour la DB
  //  - Retourne { success: true, tier, period, customerInfo }
  try {
    const tier   = planId.replace('_yearly', '');
    const period = planId.includes('_yearly') ? 'yearly' : 'monthly';

    console.log('[activateSubscription] 🛒 achat RC:', tier, period);
    const { purchaseSubscription } = require('./IAPService');
    const result = await purchaseSubscription(tier, period, user);

    if (result?.cancelled) {
      console.log('[activateSubscription] achat annulé par l\'utilisateur');
      return { success: false, cancelled: true };
    }

    if (!result?.success) {
      console.warn('[activateSubscription] achat échoué:', result);
      Alert.alert(
        'Achat impossible',
        "Le paiement n'a pas pu aboutir. Réessayez plus tard ou contactez le support."
      );
      return { success: false };
    }

    // Achat validé par Play Store + RC → mettre à jour UI
    try {
      const { saveTierLocally } = require('./PremiumService');
      const uid = user?.id || user?.sub;
      if (uid) await saveTierLocally(uid, tier);
    } catch {}
    if (updateUserProfile) updateUserProfile({ tier });

    const labels = { basic: 'Basic ⭐', premium: 'Premium ✨', gold: 'Gold 👑', free: 'Gratuit' };
    const label  = labels[tier] || tier.toUpperCase();
    Alert.alert(
      '✅ Abonnement actif',
      `Votre plan ${label}${period === 'yearly' ? ' annuel' : ''} est désormais actif. Merci !`,
    );
    return { success: true, tier, period };
  } catch (e) {
    console.error('[activateSubscription] error:', e);
    // Si l'utilisateur a annulé l'achat sur l'UI Google Play
    if (e.userCancelled) {
      return { success: false, cancelled: true };
    }
    Alert.alert('Erreur', e.message || "Impossible d'activer l'abonnement.");
    return { success: false, error: e.message };
  }
}

export { TEST_MODE };
